import { Button, Icon, ActionsDropdown, Input } from '@aidkitorg/component-library';
import { SupportedLanguage, SupportedLanguages, LanguageNames, STRINGS, RtlLanguages } from '@aidkitorg/i18n/lib';
import * as React from 'react';

const populateLanguageKeys = (object: object, languageKeys: Record<string, string>, prefix: string = '') => {
  Object.entries(object).forEach(([key, value]) => {
    if (typeof value === 'string') {
      languageKeys[`${prefix}${key}`] = value;
    } else {
      populateLanguageKeys(value, languageKeys, `${prefix}${key}.`);
    }
  });
};

export const TranslationOverview: React.FC = () => {
  const [languages, setLanguages] = React.useState<SupportedLanguage[]>(['en']);
  const [searchText, setSearchText] = React.useState('');

  const translations = React.useMemo(() => {
    const translationsByLanguage: Record<string, Record<string, string>> = {};

    SupportedLanguages.forEach((lang) => {
      const languageKeys: Record<string, string> = {};
      populateLanguageKeys(STRINGS[lang], languageKeys);

      Object.entries(languageKeys).forEach(([key, value]) => {
        translationsByLanguage[key] ||= {};
        translationsByLanguage[key][lang] = value;
      });
    });

    return translationsByLanguage;
  }, []);

  const filteredTranslations = React.useMemo(() => {
    const filteredTranslations: Record<string, Record<string, string>> = {};

    Object.entries(translations).forEach(([key, translations]) => {
      if (languages.some((lang) => translations[lang].toLocaleLowerCase().includes(searchText.toLocaleLowerCase()))) {
        filteredTranslations[key] = translations;
      }
    });

    return filteredTranslations;
  }, [translations, searchText]);

  const addLanguageActions = React.useMemo(() => {
    const languagesSet = new Set(languages);
    const unusedLanguages = SupportedLanguages.filter((lang) => !languagesSet.has(lang));
    const sortedLanguages = unusedLanguages.sort((langA, langB) => LanguageNames[langA].localeCompare(LanguageNames[langB]));
    return sortedLanguages.map((lang) => ({
      label: LanguageNames[lang],
      onClick: () => setLanguages((languages) => [...languages, lang]),
    }));
  }, [languages]);

  const removeLanguage = React.useCallback((langToRemove: SupportedLanguage) => {
    setLanguages((languages) => {
      return languages.filter((lang) => lang !== langToRemove);
    });
  }, []);

  const getCellClassName = React.useCallback((lang: SupportedLanguage) => {
    return "pr-3 " + (RtlLanguages.includes(lang) ? 'text-right' : ''); 
  }, []);

  return <div>
    <Input
      className="mb-3"
      label='Search for specific translations'
      placeholder='Search...'
      value={searchText}
      onChange={(e) => setSearchText(e.currentTarget.value)}
    />
    <table>
      <thead>
        <tr className="border-b border-gray-300">
          {languages.map((lang) => (
            <th className={getCellClassName(lang)}>
              <div className="inline-flex items-center">
                <span>{LanguageNames[lang]}</span>
                {lang !== 'en' && <Button
                  variant='unstyled'
                  icon={<Icon size="sm" name="XMarkIcon"/>}
                  onClick={() => removeLanguage(lang)}
                />}
              </div>
            </th>
          ))}
          <th className={getCellClassName('en')}>
            <ActionsDropdown
              actions={addLanguageActions}
              item={undefined}
              triggerIcon={<Icon size="md" name="PlusIcon" />}
            />
          </th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(filteredTranslations).map(([key, translations]) => (
          <tr key={key} className="hover:bg-gray-100 border-b border-gray-300">
            {languages.map((lang) => (
              <td key={`${key}:${lang}`} className={getCellClassName(lang)}>
                {translations[lang]}  
              </td>
            ))} 
          </tr>
        ))}
      </tbody>
    </table>
  </div>;
};
